import { Label } from 'components/typography';
import { useEffectOnce } from 'lib/useOnMount';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { isClientSide } from 'utils/isClientSide';

export const TextAreaStyle = styled.textarea`
  background-color: ${({ theme }) => theme.colors.input.default.background};
  border: none;
  color: ${({ theme }) => theme.colors.input.default.text};
  font-size: ${({ theme }) => theme.typography.body.medium.size.web};
  font-weight: ${({ theme }) => theme.typography.body.medium.weight};
  line-height: ${({ theme }) => theme.typography.body.medium.lineHeight};
  font-style: ${({ theme }) => theme.typography.body.medium.style};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.colors.input.default.boxShadow};
  box-sizing: border-box;
  padding: 0.75rem;
  padding-bottom: 1.75rem;
  position: relative;
  resize: none;
  vertical-align: top;
  width: 100%;

  /* Font size need to be 16px for iPhone to not zoom on focus */
  font-size: 16px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.input.hover.background};
    box-shadow: ${({ theme }) => theme.colors.input.hover.boxShadow};
    color: ${({ theme }) => theme.colors.input.hover.text};
  }

  &:focus {
    background-color: ${({ theme }) => theme.colors.input.focus.background};
    box-shadow: ${({ theme }) => theme.colors.input.focus.boxShadow};
    color: ${({ theme }) => theme.colors.input.focus.text};
  }
  &:focus-visible {
    outline: none;
  }
  &::placeholder {
    color: ${({ theme }) => theme.colors.input.default.placeholder};
  }

  &:invalid {
    background-color: ${({ theme }) => theme.colors.input.error.background};
    color: ${({ theme }) => theme.colors.input.error.text};
    box-shadow: ${({ theme }) => theme.colors.input.error.boxShadow};
    + div > label {
      color: ${({ theme }) => theme.colors.input.error.text};
    }
  }
  &:disabled {
    background-color: ${({ theme }) => theme.colors.input.disabled.background};
    box-shadow: ${({ theme }) => theme.colors.input.disabled.boxShadow};
    color: ${({ theme }) => theme.colors.input.disabled.text};
    cursor: not-allowed;
    + div > svg {
      color: ${({ theme }) => theme.colors.input.disabled.text};
    }
    + div > label {
      color: ${({ theme }) => theme.colors.input.disabled.text};
    }
  }
`;

type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & { maxLenght?: number };

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ maxLength, ...props }: TextAreaProps, ref) => {
    const [length, setLength] = useState(0);
    const ref2 = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ref2.current as HTMLTextAreaElement);

    useEffectOnce(() => {
      if (isClientSide()) {
        const textArea = document.querySelector('textarea');
        setLength(textArea?.value.length ?? 0);
      }
    });

    const onChange = e => {
      setLength(e.target.value.length);
      props.onChange?.(e);
    };

    if (maxLength) {
      const isOverMaxLength = length > maxLength;
      return (
        <div className="position relative text-input-default-text">
          <TextAreaStyle ref={ref2} aria-label="Input text" {...props} onChange={onChange} />
          <div className="block absolute bottom-3 right-4">
            <Label
              size="specialSmall"
              className={isOverMaxLength ? 'text-greyscale-white' : 'text-greyscale-lightGrey'}>
              {length || 0}/{maxLength}
            </Label>
          </div>
        </div>
      );
    }
    return <TextAreaStyle ref={ref2} {...props} />;
  }
);
